/**
 * Big 3 Astrological Calculator — Shopify section build
 *
 * Computes Sun, Moon and Ascendant zodiac signs from birth data.
 *
 * Formulas from Meeus, "Astronomical Algorithms" (2nd ed):
 *   - Sun: simplified low-precision series (Ch. 25), accuracy ~0.01°
 *   - Moon: truncated ELP2000 (Ch. 47), accuracy ~0.2° in longitude
 *   - GMST: Ch. 12.4, accuracy sub-arcsec
 *   - Ascendant: Ch. 13.4
 *
 * Mounts on the first [data-astro-id] element found in the DOM.
 * Additional instances on the same page are ignored (warned to console).
 */

(function () {
  'use strict';

  // =============================================================
  // ZODIAC CONSTANTS
  // =============================================================

  const SIGNS = [
    { name: 'Ariete',      element: 'fuoco', modality: 'cardinale', symbol: '♈' },
    { name: 'Toro',        element: 'terra', modality: 'fisso',     symbol: '♉' },
    { name: 'Gemelli',     element: 'aria',  modality: 'mobile',    symbol: '♊' },
    { name: 'Cancro',      element: 'acqua', modality: 'cardinale', symbol: '♋' },
    { name: 'Leone',       element: 'fuoco', modality: 'fisso',     symbol: '♌' },
    { name: 'Vergine',     element: 'terra', modality: 'mobile',    symbol: '♍' },
    { name: 'Bilancia',    element: 'aria',  modality: 'cardinale', symbol: '♎' },
    { name: 'Scorpione',   element: 'acqua', modality: 'fisso',     symbol: '♏' },
    { name: 'Sagittario',  element: 'fuoco', modality: 'mobile',    symbol: '♐' },
    { name: 'Capricorno',  element: 'terra', modality: 'cardinale', symbol: '♑' },
    { name: 'Acquario',    element: 'aria',  modality: 'fisso',     symbol: '♒' },
    { name: 'Pesci',       element: 'acqua', modality: 'mobile',    symbol: '♓' },
  ];

  const SUN_FLAVOR = {
    Ariete:     'La tua identità è diretta, pionieristica, combattiva.',
    Toro:       'Ti radichi nei cinque sensi, nella costanza, nel piacere concreto.',
    Gemelli:    'Sei curiosità in movimento: parole, idee, connessioni.',
    Cancro:     'La tua essenza è protettiva, emotiva, legata alle radici.',
    Leone:      'Ti esprimi con generosità, calore, vocazione scenica.',
    Vergine:    'Sei precisione, servizio, cura dei dettagli.',
    Bilancia:   'Vivi di armonia, relazioni autentiche, senso estetico.',
    Scorpione:  'La tua profondità è trasformativa, intensa, senza filtri.',
    Sagittario: 'Sei visione ampia, libertà, ricerca di significato.',
    Capricorno: 'Sei disciplina, ambizione, costruzione paziente.',
    Acquario:   'Sei originalità, indipendenza, con uno sguardo rivolto al collettivo.',
    Pesci:      'La tua sensibilità è dolce, la tua immaginazione non conosce limiti.',
  };

  const MOON_FLAVOR = {
    Ariete:     'Le tue emozioni arrivano rapide, dirette, senza mediazione.',
    Toro:       'Hai bisogno di stabilità e comfort per sentirti al sicuro.',
    Gemelli:    'Elabori i sentimenti parlandone, nominandoli, analizzandoli.',
    Cancro:     'Vivi le relazioni con particolare profondità emotiva.',
    Leone:      'Hai bisogno di riconoscimento e apprezzamenti sinceri.',
    Vergine:    'Le tue emozioni passano attraverso la cura pratica.',
    Bilancia:   'Cerchi armonia e benessere nelle relazioni autentiche.',
    Scorpione:  'Senti tutto in profondità, niente per te rimane in superficie.',
    Sagittario: 'Ti ricarichi con i viaggi, l’avventura e la scoperta di nuovi orizzonti.',
    Capricorno: 'Controlli le tue emozioni e ti proteggi con la struttura.',
    Acquario:   'I tuoi sentimenti passano attraverso il filtro della mente.',
    Pesci:      'Assorbi l’atmosfera, cogli le vibrazioni, hai una sensibilità sopraffina.',
  };

  const ASC_FLAVOR = {
    Ariete:     'Ti presenti subito con un’energia diretta e sicura.',
    Toro:       'Trasmetti un’impressione calma e molto rassicurante.',
    Gemelli:    'Raggiungi gli altri con le tue doti comunicative e la tua vivacità.',
    Cancro:     'Trasmetti dolcezza e protezione, fin dal primo sguardo.',
    Leone:      'Hai una presenza calorosa, magnetica e luminosa.',
    Vergine:    'Gli altri percepiscono la tua discrezione, il tuo ordine misurato.',
    Bilancia:   'Il tuo stile è elegante e conciliante, sei grazia pura.',
    Scorpione:  'La tua aura è intensa, penetrante, un po’ misteriosa.',
    Sagittario: 'Gli altri percepiscono la tua apertura, l’ottimismo, l’espansività.',
    Capricorno: 'Le persone sentono subito che di te ci si può fidare.',
    Acquario:   'La tua presenza è creativa, fantasiosa, libera.',
    Pesci:      'Hai un’aria sognante, trasmetti dolcezza ed empatia.',
  };

  // =============================================================
  // MATH HELPERS
  // =============================================================

  const PI = Math.PI;
  const deg2rad = (d) => d * PI / 180;
  const rad2deg = (r) => r * 180 / PI;
  const normDeg = (d) => ((d % 360) + 360) % 360;

  // =============================================================
  // JULIAN DAY (from UTC calendar date + time)
  // Meeus Ch. 7
  // =============================================================

  function julianDay(year, month, day, hour, minute, second) {
    if (month <= 2) { year -= 1; month += 12; }
    const a = Math.floor(year / 100);
    const b = 2 - a + Math.floor(a / 4);
    const jd = Math.floor(365.25 * (year + 4716))
      + Math.floor(30.6001 * (month + 1))
      + day + b - 1524.5
      + (hour + minute / 60 + second / 3600) / 24;
    return jd;
  }

  // =============================================================
  // SUN APPARENT ECLIPTIC LONGITUDE
  // Meeus Ch. 25 (low precision, ~0.01° accuracy)
  // =============================================================

  function sunLongitude(T) {
    const L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T;
    const M = 357.52911 + 35999.05029 * T - 0.0001537 * T * T;
    const Mr = deg2rad(M);
    const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(Mr)
      + (0.019993 - 0.000101 * T) * Math.sin(2 * Mr)
      + 0.000289 * Math.sin(3 * Mr);
    const trueLon = L0 + C;
    const omega = 125.04 - 1934.136 * T;
    const apparent = trueLon - 0.00569 - 0.00478 * Math.sin(deg2rad(omega));
    return normDeg(apparent);
  }

  // =============================================================
  // MOON APPARENT ECLIPTIC LONGITUDE
  // Meeus Ch. 47 — truncated ELP2000 (most significant 40 terms)
  // Accuracy ~0.2° in longitude (fine for sign determination)
  // =============================================================

  function moonLongitude(T) {
    const Lp = 218.3164477 + 481267.88123421 * T - 0.0015786 * T * T
      + T * T * T / 538841 - T * T * T * T / 65194000;
    const D = 297.8501921 + 445267.1114034 * T - 0.0018819 * T * T
      + T * T * T / 545868 - T * T * T * T / 113065000;
    const M = 357.5291092 + 35999.0502909 * T - 0.0001536 * T * T
      + T * T * T / 24490000;
    const Mp = 134.9633964 + 477198.8675055 * T + 0.0087414 * T * T
      + T * T * T / 69699 - T * T * T * T / 14712000;
    const F = 93.2720950 + 483202.0175233 * T - 0.0036539 * T * T
      - T * T * T / 3526000 + T * T * T * T / 863310000;

    const E = 1 - 0.002516 * T - 0.0000074 * T * T;

    const terms = [
      [0,  0,  1,  0,  6288774],
      [2,  0, -1,  0,  1274027],
      [2,  0,  0,  0,   658314],
      [0,  0,  2,  0,   213618],
      [0,  1,  0,  0,  -185116],
      [0,  0,  0,  2,  -114332],
      [2,  0, -2,  0,    58793],
      [2, -1, -1,  0,    57066],
      [2,  0,  1,  0,    53322],
      [2, -1,  0,  0,    45758],
      [0,  1, -1,  0,   -40923],
      [1,  0,  0,  0,   -34720],
      [0,  1,  1,  0,   -30383],
      [2,  0,  0, -2,    15327],
      [0,  0,  1,  2,   -12528],
      [0,  0,  1, -2,    10980],
      [4,  0, -1,  0,    10675],
      [0,  0,  3,  0,    10034],
      [4,  0, -2,  0,     8548],
      [2,  1, -1,  0,    -7888],
      [2,  1,  0,  0,    -6766],
      [1,  0, -1,  0,    -5163],
      [1,  1,  0,  0,     4987],
      [2, -1,  1,  0,     4036],
      [2,  0,  2,  0,     3994],
      [4,  0,  0,  0,     3861],
      [2,  0, -3,  0,     3665],
      [0,  1, -2,  0,    -2689],
      [2,  0, -1,  2,    -2602],
      [2, -1, -2,  0,     2390],
      [1,  0,  1,  0,    -2348],
      [2, -2,  0,  0,     2236],
      [0,  1,  2,  0,    -2120],
      [0,  2,  0,  0,    -2069],
      [2, -2, -1,  0,     2048],
      [2,  0,  1, -2,    -1773],
      [2,  0,  0,  2,    -1595],
      [4, -1, -1,  0,     1215],
      [0,  0,  2,  2,    -1110],
      [3,  0, -1,  0,     -892],
    ];

    let sumL = 0;
    for (const [dCoef, mCoef, mpCoef, fCoef, amp] of terms) {
      const arg = deg2rad(dCoef * D + mCoef * M + mpCoef * Mp + fCoef * F);
      let term = amp * Math.sin(arg);
      if (mCoef !== 0) term *= Math.pow(E, Math.abs(mCoef));
      sumL += term;
    }

    return normDeg(Lp + sumL / 1000000);
  }

  // =============================================================
  // MEAN OBLIQUITY OF ECLIPTIC
  // IAU 1980, Meeus 22.2 (degrees)
  // =============================================================

  function meanObliquity(T) {
    const secArc = 21.448 - 46.8150 * T - 0.00059 * T * T + 0.001813 * T * T * T;
    return 23 + 26 / 60 + secArc / 3600;
  }

  // =============================================================
  // GREENWICH MEAN SIDEREAL TIME (degrees)
  // Meeus 12.4
  // =============================================================

  function gmst(jd) {
    const T = (jd - 2451545.0) / 36525;
    const theta = 280.46061837
      + 360.98564736629 * (jd - 2451545.0)
      + 0.000387933 * T * T
      - T * T * T / 38710000;
    return normDeg(theta);
  }

  // =============================================================
  // ASCENDANT — ecliptic longitude rising on the eastern horizon
  // Meeus 13.4
  // =============================================================

  function ascendant(lstDeg, latDeg, oblDeg) {
    const lst = deg2rad(lstDeg);
    const lat = deg2rad(latDeg);
    const obl = deg2rad(oblDeg);
    const y = Math.cos(lst);
    const x = -(Math.sin(lst) * Math.cos(obl) + Math.tan(lat) * Math.sin(obl));
    return normDeg(rad2deg(Math.atan2(y, x)));
  }

  // =============================================================
  // SIGN MAPPING
  // =============================================================

  function signFromLongitude(lon) {
    const idx = Math.floor(normDeg(lon) / 30);
    const degInSign = normDeg(lon) - idx * 30;
    const deg = Math.floor(degInSign);
    const minFloat = (degInSign - deg) * 60;
    const min = Math.floor(minFloat);
    return {
      ...SIGNS[idx],
      longitude: lon,
      degInSign,
      displayDeg: `${deg}° ${String(min).padStart(2, '0')}′`,
    };
  }

  // =============================================================
  // TIMEZONE HANDLING
  // =============================================================

  function getTimezoneOffsetMinutes(date, timezone) {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const parts = {};
    dtf.formatToParts(date).forEach(p => {
      if (p.type !== 'literal') parts[p.type] = p.value;
    });
    const asIfUTC = Date.UTC(
      +parts.year, +parts.month - 1, +parts.day,
      +parts.hour, +parts.minute, +parts.second
    );
    return (asIfUTC - date.getTime()) / 60000;
  }

  function localToUTC(year, month, day, hour, minute, timezone) {
    let guessUTC = Date.UTC(year, month - 1, day, hour, minute);
    let offset = getTimezoneOffsetMinutes(new Date(guessUTC), timezone);
    let corrected = guessUTC - offset * 60000;
    offset = getTimezoneOffsetMinutes(new Date(corrected), timezone);
    corrected = guessUTC - offset * 60000;
    return new Date(corrected);
  }

  // =============================================================
  // MAIN CALCULATION ENTRY POINT
  // =============================================================

  function calculateBig3({ year, month, day, hour, minute, lat, lon, timezone }) {
    const utc = localToUTC(year, month, day, hour, minute, timezone);
    const jd = julianDay(
      utc.getUTCFullYear(), utc.getUTCMonth() + 1, utc.getUTCDate(),
      utc.getUTCHours(), utc.getUTCMinutes(), utc.getUTCSeconds()
    );
    const T = (jd - 2451545.0) / 36525;

    const sunLon = sunLongitude(T);
    const moonLon = moonLongitude(T);
    const obl = meanObliquity(T);
    const gst = gmst(jd);
    const lst = normDeg(gst + lon);
    const ascLon = ascendant(lst, lat, obl);

    return {
      sun: signFromLongitude(sunLon),
      moon: signFromLongitude(moonLon),
      ascendant: signFromLongitude(ascLon),
      utcUsed: utc,
    };
  }

  // =============================================================
  // STATE & UI
  // =============================================================

  let rootEl = null;

  const state = {
    view: 'form',
    form: { day: '', month: '', year: '', hour: '', minute: '', cityQuery: '', city: null },
    results: null,
    error: null,
  };

  function render() {
    if (!rootEl) return;
    rootEl.innerHTML = '';

    if (state.view === 'form') {
      renderForm(rootEl);
    } else {
      renderResults(rootEl);
    }

    const foot = document.createElement('p');
    foot.className = 'footnote';
    foot.innerHTML = 'Calcoli astronomici eseguiti localmente nel tuo browser &middot; Nessun dato inviato a server esterni<br>Dati geografici: <a href="https://www.geonames.org" style="color:inherit" target="_blank" rel="noopener">GeoNames.org</a> (CC&#8209;BY&nbsp;4.0)';
    rootEl.appendChild(foot);
  }

  function renderForm(parent) {
    const form = document.createElement('form');
    form.className = 'form-card';
    form.noValidate = true;

    form.innerHTML = `
      <div class="field-group">
        <label>Data di nascita</label>
        <div class="grid-3">
          <input type="number" name="day" placeholder="DD" min="1" max="31" required value="${state.form.day}">
          <input type="number" name="month" placeholder="MM" min="1" max="12" required value="${state.form.month}">
          <input type="number" name="year" placeholder="YYYY" min="1900" max="2100" required value="${state.form.year}">
        </div>
      </div>
      <div class="field-group">
        <label>Ora di nascita (locale)</label>
        <div class="grid-2">
          <input type="number" name="hour" placeholder="HH" min="0" max="23" required value="${state.form.hour}">
          <input type="number" name="minute" placeholder="MM" min="0" max="59" required value="${state.form.minute}">
        </div>
      </div>
      <div class="field-group autocomplete-wrap">
        <label>Città di nascita</label>
        <input type="text" name="city" placeholder="Inizia a digitare una città italiana&hellip;" autocomplete="off" required value="${escapeHtml(state.form.cityQuery)}">
        <div class="autocomplete-list" hidden></div>
      </div>
      ${state.error ? `<div class="error">${escapeHtml(state.error)}</div>` : ''}
      <button type="submit">Calcola il mio Big 3</button>
    `;

    parent.appendChild(form);

    const cityInput = form.querySelector('input[name="city"]');
    const listEl = form.querySelector('.autocomplete-list');
    setupAutocomplete(cityInput, listEl);

    form.addEventListener('submit', handleSubmit);
    form.querySelectorAll('input[type="number"]').forEach(inp => {
      inp.addEventListener('input', (e) => {
        state.form[e.target.name] = e.target.value;
      });
    });
  }

  function renderResults(parent) {
    const { results } = state;
    if (!results) return;

    const grid = document.createElement('div');
    grid.className = 'result-grid';
    grid.appendChild(resultCard('Sole', results.sun, SUN_FLAVOR));
    grid.appendChild(resultCard('Luna', results.moon, MOON_FLAVOR));
    grid.appendChild(resultCard('Ascendente', results.ascendant, ASC_FLAVOR));
    parent.appendChild(grid);

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.innerHTML = `<button class="ghost" id="back">Nuovo calcolo</button>`;
    parent.appendChild(actions);

    actions.querySelector('#back').addEventListener('click', () => {
      state.view = 'form';
      state.results = null;
      state.error = null;
      render();
    });
  }

  function resultCard(kind, sign, flavorMap) {
    const el = document.createElement('div');
    el.className = 'result-card';
    el.innerHTML = `
      <div class="kind">${kind}</div>
      <h3 class="sign">${sign.name}</h3>
      <div class="detail">${sign.displayDeg} &middot; elemento ${sign.element} &middot; ${sign.modality}</div>
      <div class="flavor">${flavorMap[sign.name]}</div>
    `;
    return el;
  }

  // =============================================================
  // AUTOCOMPLETE
  // =============================================================

  function setupAutocomplete(input, listEl) {
    let activeIdx = -1;
    let matches = [];

    const close = () => {
      listEl.hidden = true;
      listEl.innerHTML = '';
      activeIdx = -1;
      matches = [];
    };

    const renderList = () => {
      if (matches.length === 0) { close(); return; }
      listEl.innerHTML = matches.map((c, i) => `
        <div class="autocomplete-item${i === activeIdx ? ' active' : ''}" data-idx="${i}">
          ${escapeHtml(c.name)}<span class="province">${c.province}</span>
        </div>
      `).join('');
      listEl.hidden = false;
      listEl.querySelectorAll('.autocomplete-item').forEach(item => {
        item.addEventListener('mousedown', (e) => {
          e.preventDefault();
          const idx = +item.dataset.idx;
          selectCity(matches[idx]);
        });
      });
    };

    const selectCity = (c) => {
      state.form.city = c;
      state.form.cityQuery = `${c.name} (${c.province})`;
      input.value = state.form.cityQuery;
      close();
    };

    input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase();
      state.form.cityQuery = input.value;
      state.form.city = null;
      if (q.length < 2) { close(); return; }
      matches = CITIES
        .filter(c => c.name.toLowerCase().startsWith(q) || c.name.toLowerCase().includes(' ' + q))
        .slice(0, 8);
      activeIdx = -1;
      renderList();
    });

    input.addEventListener('keydown', (e) => {
      if (listEl.hidden) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIdx = Math.min(activeIdx + 1, matches.length - 1);
        renderList();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIdx = Math.max(activeIdx - 1, 0);
        renderList();
      } else if (e.key === 'Enter') {
        if (activeIdx >= 0) {
          e.preventDefault();
          selectCity(matches[activeIdx]);
        }
      } else if (e.key === 'Escape') {
        close();
      }
    });

    input.addEventListener('blur', () => setTimeout(close, 150));
  }

  // =============================================================
  // SUBMIT HANDLER
  // =============================================================

  function handleSubmit(e) {
    e.preventDefault();
    const f = state.form;
    const day = parseInt(f.day, 10);
    const month = parseInt(f.month, 10);
    const year = parseInt(f.year, 10);
    const hour = parseInt(f.hour, 10);
    const minute = parseInt(f.minute, 10);

    if (!validDate(year, month, day)) {
      state.error = 'Data non valida. Controlla giorno, mese e anno.';
      render();
      return;
    }
    if (isNaN(hour) || hour < 0 || hour > 23 || isNaN(minute) || minute < 0 || minute > 59) {
      state.error = 'Ora non valida. Inserisci ore (0-23) e minuti (0-59).';
      render();
      return;
    }
    if (!f.city) {
      state.error = 'Seleziona una città dall’elenco suggerito.';
      render();
      return;
    }

    try {
      state.results = calculateBig3({
        year, month, day, hour, minute,
        lat: f.city.lat,
        lon: f.city.lon,
        timezone: f.city.tz,
      });
      state.error = null;
      state.view = 'results';
      render();
    } catch (err) {
      console.error(err);
      state.error = 'Errore nel calcolo: ' + err.message;
      render();
    }
  }

  function validDate(y, m, d) {
    if (isNaN(y) || isNaN(m) || isNaN(d)) return false;
    if (y < 1900 || y > 2100) return false;
    if (m < 1 || m > 12) return false;
    const daysInMonth = new Date(y, m, 0).getDate();
    return d >= 1 && d <= daysInMonth;
  }

  // =============================================================
  // UTILS
  // =============================================================

  function pad2(v) { return String(v).padStart(2, '0'); }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // =============================================================
  // CITIES — loaded from external cities.js (window.CITIES)
  // Generated from GeoNames IT (CC-BY 4.0), ~7,900 Italian comuni
  // =============================================================

  const CITIES = (typeof window !== 'undefined' && Array.isArray(window.CITIES))
    ? window.CITIES
    : [];

  // =============================================================
  // BOOTSTRAP
  // Mount on first [data-astro-id] element. Additional instances
  // on the same page are ignored (single-instance guard).
  // =============================================================

  function init() {
    const roots = document.querySelectorAll('[data-astro-id]');
    if (!roots.length) return;
    if (roots.length > 1) {
      console.warn('[astro-big3] Multiple instances detected, initializing only the first.');
    }
    rootEl = roots[0];
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
